import { useState } from 'react';
import { ticketsAPI, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShieldAlert, Loader2, CheckCircle2, XCircle, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';

/**
 * Autorizar (o no) una herramienta que un agente quiso ejecutar.
 *
 * **Lo que la pantalla tiene que mostrar antes que los botones: qué se va a ejecutar.** Una
 * aprobación en la que no se ve el contenido no es una aprobación, es una firma en blanco — y
 * quedaría registrado que esta persona autorizó algo que no pudo leer.
 *
 * **Y no se pueden editar los parámetros acá.** No es una omisión: el backend ejecuta los que
 * quedaron guardados cuando el agente lo pidió. Si la pantalla ofreciera cambiarlos, se
 * aprobaría una cosa y se ejecutaría otra.
 *
 * Rechazar pide un motivo, que es la mitad útil del rechazo: sin él, el próximo que mire no
 * sabe si hay que insistir, corregir algo o dejarlo.
 */
export default function AprobarHerramienta({ tarea, tenantId, capsuleId, ticketId, onCambio }) {
  const [ocupado, setOcupado] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [confirmar, setConfirmar] = useState({ open: false });
  const [resultado, setResultado] = useState(null);

  const carga = tarea?.input_payload || {};
  const yaCerrada = tarea?.status === 'done';
  const salida = tarea?.output_json || null;

  const aprobar = async () => {
    setOcupado(true);
    try {
      const { data } = await ticketsAPI.aprobarHerramienta(tenantId, capsuleId, ticketId, tarea.task_id);
      setResultado(data?.resultado || null);
      const ok = data?.resultado?.success;
      // Que se ejecutó no significa que salió bien. Decir "autorizada" a secas dejaría a la
      // persona creyendo que se hizo cuando el sistema remoto devolvió un error.
      if (ok) toast.success('Autorizada y ejecutada');
      else toast.error('Autorizada, pero la ejecución falló');
      if (onCambio) onCambio();
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo autorizar');
    } finally {
      setOcupado(false);
      setConfirmar({ open: false });
    }
  };

  const rechazar = async () => {
    setOcupado(true);
    try {
      await ticketsAPI.rechazarHerramienta(tenantId, capsuleId, ticketId, tarea.task_id, motivo.trim());
      toast.success('Rechazada');
      if (onCambio) onCambio();
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo rechazar');
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="border border-amber-300 bg-amber-50/60 rounded-sm p-3 space-y-3"
         data-testid="aprobar-herramienta">
      <div className="flex items-start gap-2">
        <ShieldAlert className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-xs font-semibold text-amber-900">
            Un agente quiere ejecutar <span className="font-mono">{carga.herramienta || '—'}</span>
          </p>
          <p className="text-[11px] text-amber-800">
            No se ejecutó. Revisá qué va a hacer antes de autorizar.
          </p>
        </div>
        {carga.riesgo && (
          <Badge variant="outline" className="text-[9px] rounded-sm border-amber-400 text-amber-800 shrink-0">
            riesgo {carga.riesgo}
          </Badge>
        )}
      </div>

      {/* Los parámetros exactos, y no editables: son los que se van a ejecutar. */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-amber-800 mb-1">
          Lo que se va a ejecutar
        </p>
        <pre className="bg-white border border-amber-200 rounded-sm p-2 text-[11px] overflow-x-auto max-h-48"
             data-testid="aprobacion-params">
{JSON.stringify(carga.params || {}, null, 2)}
        </pre>
      </div>

      {yaCerrada ? (
        <div className="text-[11px] text-zinc-700 bg-white border border-brand-border rounded-sm p-2 space-y-1"
             data-testid="aprobacion-cerrada">
          <div className="flex items-center gap-2">
            {salida?.success ? <CheckCircle2 className="w-3 h-3 text-green-600" />
                             : <XCircle className="w-3 h-3 text-zinc-400" />}
            <span>{tarea.output_text || 'Cerrada'}</span>
          </div>
          {tarea.aprobada_por && (
            <div className="text-[10px] text-zinc-500">Por {tarea.aprobada_por}</div>
          )}
        </div>
      ) : (
        <>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-amber-800 mb-1">
              Motivo (si la rechazás)
            </p>
            <input
              data-testid="aprobacion-motivo" value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className="w-full border border-amber-200 rounded-sm px-2 h-7 text-xs bg-white"
              placeholder="Por que no corresponde. Sin esto, el que mire despues no sabe si insistir."
            />
          </div>
          <div className="flex gap-2">
            <Button
              size="sm" data-testid="aprobacion-aprobar" disabled={ocupado}
              className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-7 px-3 gap-1"
              onClick={() => setConfirmar({
                open: true,
                title: `Autorizar ${carga.herramienta}`,
                description:
                  `Se va a ejecutar de verdad contra el sistema externo, con los parámetros de ` +
                  `arriba. Lo que cree o modifique queda.`,
                confirmText: 'Autorizar y ejecutar',
                onConfirm: aprobar,
              })}
            >
              {ocupado ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Autorizar
            </Button>
            <Button
              size="sm" variant="outline" data-testid="aprobacion-rechazar" disabled={ocupado}
              className="h-7 text-xs rounded-sm gap-1" onClick={rechazar}
            >
              <X className="w-3 h-3" /> Rechazar
            </Button>
          </div>
        </>
      )}

      {resultado && (
        <div data-testid="aprobacion-resultado"
             className={`text-[11px] rounded-sm border p-2 ${
               resultado.success ? 'border-green-200 bg-green-50 text-green-900'
                                 : 'border-red-200 bg-red-50 text-red-900'}`}>
          {resultado.success
            ? <>Ejecutada. Respondió {resultado.status_code}.</>
            : <>No se pudo ejecutar: {String(resultado.error).slice(0, 300)}</>}
        </div>
      )}

      <ConfirmActionDialog
        {...confirmar}
        onOpenChange={(open) => !open && setConfirmar({ open: false })}
      />
    </div>
  );
}
